/**
 * Browser-side PCM helpers. Capture prefers an AudioContext running at
 * 16 kHz (the browser resamples well); when a browser refuses that, frames
 * arrive at the device rate and go through this anti-aliased resampler
 * (same design as engine/interligne/audio.py).
 */

export function floatToPcm16(input: Float32Array): ArrayBuffer {
  const out = new ArrayBuffer(input.length * 2);
  const view = new DataView(out);
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i] ?? 0));
    view.setInt16(i * 2, Math.round(s < 0 ? s * 0x8000 : s * 0x7fff), true);
  }
  return out;
}

export function levels(input: Float32Array): { db: number; peak: number } {
  let acc = 0;
  let peak = 0;
  for (let i = 0; i < input.length; i += 1) {
    const v = input[i] ?? 0;
    acc += v * v;
    const a = Math.abs(v);
    if (a > peak) peak = a;
  }
  const rms = input.length ? Math.sqrt(acc / input.length) : 0;
  return { db: 20 * Math.log10(Math.max(rms, 1e-6)), peak };
}

function lowpass(cutoff: number, taps: number): Float32Array {
  const n = taps | 1;
  const h = new Float32Array(n);
  let sum = 0;
  for (let i = 0; i < n; i += 1) {
    const x = i - (n - 1) / 2;
    const sinc = x === 0 ? 1 : Math.sin(2 * Math.PI * cutoff * x) / (2 * Math.PI * cutoff * x);
    const blackman = 0.42 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1)) + 0.08 * Math.cos((4 * Math.PI * i) / (n - 1));
    h[i] = 2 * cutoff * sinc * blackman;
    sum += h[i]!;
  }
  for (let i = 0; i < n; i += 1) h[i] = h[i]! / sum;
  return h;
}

export class StreamResampler {
  readonly passthrough: boolean;
  private readonly step: number;
  private readonly h: Float32Array | null;
  private hist: Float32Array;
  private carry: number | null = null;
  private pos = 0;

  constructor(srcRate: number, dstRate = 16000, taps = 63) {
    if (srcRate <= 0 || dstRate <= 0) throw new Error("sample rates must be positive");
    this.passthrough = srcRate === dstRate;
    this.step = srcRate / dstRate;
    this.h = srcRate > dstRate ? lowpass((0.45 * Math.min(srcRate, dstRate)) / srcRate, taps) : null;
    this.hist = new Float32Array(this.h ? this.h.length - 1 : 0);
  }

  process(x: Float32Array): Float32Array {
    if (this.passthrough || x.length === 0) return x;
    let y: Float32Array = x;
    if (this.h) {
      const h = this.h;
      const buf = new Float32Array(this.hist.length + x.length);
      buf.set(this.hist);
      buf.set(x, this.hist.length);
      y = new Float32Array(x.length);
      for (let i = 0; i < x.length; i += 1) {
        let acc = 0;
        for (let k = 0; k < h.length; k += 1) acc += buf[i + k]! * h[h.length - 1 - k]!;
        y[i] = acc;
      }
      this.hist = buf.slice(buf.length - this.hist.length);
    }
    const z = this.carry === null ? y : concat(Float32Array.of(this.carry), y);
    const last = z.length - 1;
    if (last < 1) {
      if (z.length) this.carry = z[z.length - 1]!;
      return new Float32Array(0);
    }
    const out: number[] = [];
    let p = this.pos;
    while (p < last) {
      const j = Math.floor(p);
      const f = p - j;
      out.push(z[j]! + (z[j + 1]! - z[j]!) * f);
      p += this.step;
    }
    this.pos = p - last;
    this.carry = z[last]!;
    return Float32Array.from(out);
  }
}

function concat(a: Float32Array, b: Float32Array): Float32Array {
  const out = new Float32Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
}
